import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, money, METALS, CARATS, SHAPES } from "../lib/api";
import { ProductCard } from "../components/ProductCard";

export default function BuildYourRing() {
  const [shape, setShape] = useState("Round");
  const [carat, setCarat] = useState(1);
  const [metal, setMetal] = useState("silver");
  const [quote, setQuote] = useState(null);
  const [rings, setRings] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/products", { params: { category: "rings" } }).then(({ data }) => setRings(data)).catch(() => {});
  }, []);

  useEffect(() => {
    api
      .get("/pricing/quote", { params: { shape, carat, metal, category: "rings" } })
      .then(({ data }) => setQuote(data))
      .catch(() => {});
  }, [shape, carat, metal]);

  const matching = rings.filter((r) => r.shape === shape).slice(0, 6);
  const metalLabel = METALS.find((m) => m.id === metal)?.label;
  const article = /^[AEIOU]/.test(shape) ? "an" : "a";

  return (
    <div data-testid="build-your-ring-page" className="pt-10 max-w-[1400px] mx-auto px-6 sm:px-12 min-h-screen pb-24">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
        <p className="label-track text-[#A67C3D] mb-4">The Atelier</p>
        <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-[#0A0A0A] leading-tight max-w-2xl">
          Build Your Ring
        </h1>
        <p className="text-[#737373] font-light mt-5 max-w-xl">
          Choose your diamond shape, carat weight and metal. composed to your taste,
          in Canadian dollars.
        </p>
      </motion.div>

      <div className="grid lg:grid-cols-[1fr_400px] gap-12 lg:gap-20 mt-14">
        <div className="space-y-12">
          {/* SHAPE */}
          <div>
            <p className="label-track text-[#A67C3D] mb-5">
              01. Diamond Shape · <span className="text-[#0A0A0A]">{shape}</span>
            </p>
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3">
              {SHAPES.map((s) => (
                <button
                  key={s}
                  data-testid={`build-shape-${s.toLowerCase()}`}
                  onClick={() => setShape(s)}
                  className={`py-4 label-track text-[10px] border transition-colors ${
                    shape === s
                      ? "border-[#0A0A0A] bg-[#0A0A0A] text-white"
                      : "border-[#E5E5E5] text-[#737373] hover:border-[#0A0A0A] hover:text-[#0A0A0A] bg-[#FFFFFF]"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* CARAT */}
          <div>
            <p className="label-track text-[#A67C3D] mb-5">
              02. Carat Weight · <span className="text-[#0A0A0A]">{carat}.00 ct</span>
            </p>
            <div className="flex gap-3">
              {CARATS.map((c) => (
                <button
                  key={c}
                  data-testid={`build-carat-${c}`}
                  onClick={() => setCarat(c)}
                  className={`w-14 h-14 font-mono text-sm border transition-colors ${
                    carat === c
                      ? "border-[#0A0A0A] bg-[#0A0A0A] text-white"
                      : "border-[#E5E5E5] text-[#737373] hover:border-[#0A0A0A] hover:text-[#0A0A0A] bg-[#FFFFFF]"
                  }`}
                >
                  {c}ct
                </button>
              ))}
            </div>
          </div>

          {/* METAL */}
          <div>
            <p className="label-track text-[#A67C3D] mb-5">
              03. Metal · <span className="text-[#0A0A0A]">{metalLabel}</span>
            </p>
            <div className="flex flex-wrap gap-3">
              {METALS.map((m) => (
                <button
                  key={m.id}
                  data-testid={`build-metal-${m.id}`}
                  onClick={() => setMetal(m.id)}
                  className={`px-8 py-3.5 label-track text-[11px] border transition-colors ${
                    metal === m.id
                      ? "border-[#0A0A0A] bg-[#0A0A0A] text-white"
                      : "border-[#E5E5E5] text-[#737373] hover:border-[#0A0A0A] hover:text-[#0A0A0A] bg-[#FFFFFF]"
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* SUMMARY */}
        <div className="lg:sticky lg:top-28 h-fit">
          <div className="bg-[#0A0A0A] text-white p-8">
            <p className="label-track text-[#C9A05C] mb-6">Your Creation</p>
            <div className="space-y-4 text-sm">
              <div className="flex justify-between border-b border-white/15 pb-3">
                <span className="text-white/50">Diamond</span>
                <span>{carat}.00 ct {shape}</span>
              </div>
              <div className="flex justify-between border-b border-white/15 pb-3">
                <span className="text-white/50">Metal</span>
                <span>{metalLabel}</span>
              </div>
              <div className="flex justify-between border-b border-white/15 pb-3">
                <span className="text-white/50">Setting</span>
                <span>Women's. {quote?.gram_weight || 4}g</span>
              </div>
            </div>
            <motion.p
              key={quote?.price}
              initial={{ opacity: 0.4 }}
              animate={{ opacity: 1 }}
              data-testid="build-price"
              className="font-mono text-3xl mt-8 text-[#C9A05C]"
            >
              {quote ? money(quote.price) : "…"}
            </motion.p>
            <p className="text-[10px] text-white/50 mt-2">Certified lab grown diamond · certificate issued with your purchase</p>
            <Link
              to="/shop/rings"
              data-testid="build-browse-rings"
              className="mt-8 block text-center bg-white text-[#0A0A0A] px-8 py-4 label-track transition-all duration-300 hover:bg-transparent hover:text-white hover:shadow-[inset_0_0_0_1px_#FFFFFF]"
            >
              Browse {shape} Rings
            </Link>
          </div>
        </div>
      </div>

      {matching.length > 0 && (
        <div className="mt-24">
          <p className="label-track text-[#0A0A0A] mb-3">From the collection</p>
          <h2 className="font-heading text-2xl sm:text-3xl text-[#0A0A0A] mb-10">Rings with {article} {shape} stone</h2>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12">
            {matching.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
